import {useCallback, useEffect, useRef} from 'react'

// Cloudflare Turnstile no login e no "esqueci a senha". O desafio só roda no submit (o token vale 5
// minutos e é de uso único) e o widget só aparece quando a Cloudflare pede interação.

type TurnstileOptions = {
  sitekey: string
  action: string
  execution: 'render' | 'execute'
  appearance: 'always' | 'execute' | 'interaction-only'
  callback: (token: string) => void
  'error-callback': () => void
  'expired-callback': () => void
  'timeout-callback': () => void
}

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileOptions) => string
  execute: (widgetId: string) => void
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY

export const TURNSTILE_FAILURE_MESSAGE =
  'Não foi possível confirmar que você não é um robô. Recarregue a página e tente novamente.'

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve()
  }
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => {
        // Permite tentar de novo numa próxima tela (ex.: rede caiu no primeiro carregamento).
        scriptPromise = null
        script.remove()
        reject(new Error('turnstile_script'))
      }
      document.head.appendChild(script)
    })
  }

  return scriptPromise
}

type Pending = {resolve: (token: string) => void; reject: (error: Error) => void}

function settle(pendingRef: {current?: Pending}, token: string | null, reason = 'turnstile_error') {
  const pending = pendingRef.current
  pendingRef.current = undefined
  if (!pending) return
  if (token) pending.resolve(token)
  else pending.reject(new Error(reason))
}

export function useTurnstile(action: string) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string>()
  const pendingRef = useRef<Pending>()
  const executedRef = useRef(false)

  const renderWidget = useCallback(() => {
    if (widgetIdRef.current || !containerRef.current || !window.turnstile || !SITE_KEY) return
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      action,
      execution: 'execute',
      appearance: 'interaction-only',
      callback: (token) => settle(pendingRef, token),
      'error-callback': () => settle(pendingRef, null, 'turnstile_error'),
      'expired-callback': () => settle(pendingRef, null, 'turnstile_expired'),
      'timeout-callback': () => settle(pendingRef, null, 'turnstile_timeout'),
    })
  }, [action])

  useEffect(() => {
    if (!SITE_KEY) return
    let cancelled = false
    loadScript()
      .then(() => {
        if (!cancelled) renderWidget()
      })
      .catch(() => {})

    return () => {
      cancelled = true
      settle(pendingRef, null, 'turnstile_unmounted')
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
      }
      widgetIdRef.current = undefined
      executedRef.current = false
    }
  }, [renderWidget])

  // Devolve um token novo a cada chamada. Sem site key (desenvolvimento local), devolve vazio: o backend
  // local não verifica o captcha.
  const getToken = useCallback(async (): Promise<string> => {
    if (!SITE_KEY) return ''

    await loadScript()
    renderWidget()
    const turnstile = window.turnstile
    const widgetId = widgetIdRef.current
    if (!turnstile || !widgetId) {
      throw new Error('turnstile_not_ready')
    }

    // Token é de uso único: cada nova tentativa começa de um widget limpo.
    if (executedRef.current) {
      turnstile.reset(widgetId)
    }
    settle(pendingRef, null, 'turnstile_superseded')
    executedRef.current = true

    return new Promise<string>((resolve, reject) => {
      pendingRef.current = {resolve, reject}
      turnstile.execute(widgetId)
    })
  }, [renderWidget])

  return {containerRef, getToken}
}
